import { useMutation, useQueryClient } from '@tanstack/react-query';

import type { FieldGroupPrecedence, ServicesProvider } from '@ekhodealer/ekho-common';

import { useCurrentServicesProviderId } from '@common/hooks/useCurrentServicesProviderId';
import { apiClient } from '@config/server/apiClient';
import { configurationEndpoints } from '@config/server/apiEndpoints';
import { queries } from '@config/server/queryKeys';

/**
 * Input type for creating field group precedence (without id, injected from provider)
 */
export interface CreateFieldGroupPrecedenceInput {
	fieldGroupPrecedence: FieldGroupPrecedence;
}

/**
 * API function to create inventory field group precedence for a services provider
 */
export const createInventoryFieldGroupPrecedence = async (
	servicesProviderId: string,
	input: CreateFieldGroupPrecedenceInput,
): Promise<ServicesProvider> => {
	const response = await apiClient.post<ServicesProvider>(
		configurationEndpoints.inventoryFieldGroupPrecedence(servicesProviderId),
		input,
	);
	return response.data;
};

/**
 * Hook for creating inventory field group precedence for a services provider
 */
export const useCreateInventoryFieldGroupPrecedence = () => {
	const servicesProviderId = useCurrentServicesProviderId();
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: async (input: CreateFieldGroupPrecedenceInput) => {
			if (!servicesProviderId) {
				throw new Error(
					'Unable to create field group precedence without a services provider ID.',
				);
			}
			return createInventoryFieldGroupPrecedence(servicesProviderId, input);
		},
		onSuccess: (data) => {
			// Optimistic cache update
			queryClient.setQueryData(queries.servicesProviders.detail(data.id).queryKey, data);
		},
	});
};
